/** Coalesced autosave, local recovery, retries and explicit revision conflicts. */
import type { Project } from "./core/project";
import type { DraftRecord } from "./core/workspace";
import { object } from "./core/workspace";
import { api, ApiError, checkedDraft, createDraft } from "./api";
import { clientId, forget, forgetUnchanged, persist, abandonedRecoveries, claimRecovery, releaseRecovery, type Recovery } from "./recovery";
export type SaveState = "saved" | "pending" | "saving" | "offline" | "conflict" | "recovery-unavailable";
const same = (a: Project, b: Project): boolean => JSON.stringify({ ...a, updatedAt: "" }) === JSON.stringify({ ...b, updatedAt: "" });
const live = new Set<ProjectDraft>();
export function stopDrafts(): void { for (const draft of live) draft.stop(); }
export async function flushDrafts(): Promise<void> { await Promise.all([...live].map((d) => d.flush())); }
export class ProjectDraft {
  private readonly client = clientId();
  private generation = 0;
  private dirty = false;
  private stopped = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private saving: Promise<void> | null = null;
  private local: Promise<void> = Promise.resolve();
  private retry = 1000;
  private recoveryKey: string;
  private recoveredFrom: Recovery | null = null;
  private localAvailable = true;
  private remote: DraftRecord | null = null;
  private resolving = false;
  private readonly online = (): void => { if (this.dirty) void this.flush(); };
  private constructor(readonly owner: string, private stored: DraftRecord, private current: Project,
    private name: string, private readonly changed: (state: SaveState) => void) {
    this.recoveryKey = `${owner}:${stored.id}:${this.client}`;
    live.add(this); claimRecovery(owner, stored.id); window.addEventListener("online", this.online);
  }
  static async open(owner: string, stored: DraftRecord, changed: (state: SaveState) => void): Promise<ProjectDraft> {
    const draft = new ProjectDraft(owner, stored, stored.document, stored.title, changed);
    let recovery: Recovery | undefined;
    try {
      const rows = await abandonedRecoveries(owner, stored.id);
      recovery = rows[0];
    } catch { draft.localAvailable = false; }
    if (recovery && (!same(recovery.document, stored.document) || recovery.title !== stored.title)) {
      draft.current = recovery.document; draft.name = recovery.title; draft.generation = recovery.generation;
      draft.dirty = true; draft.recoveredFrom = recovery;
      draft.queueLocal();
      if (recovery.revision !== stored.revision) { draft.remote = stored; changed("conflict"); }
      else { draft.schedule(400); }
    } else changed(draft.localAvailable ? "saved" : "recovery-unavailable");
    return draft;
  }
  get id(): string { return this.stored.id; }
  get fingerprint(): string { return this.stored.fingerprint; }
  get project(): Project { return this.current; }
  get title(): string { return this.name; }
  get hasConflict(): boolean { return this.remote !== null; }
  get recoveryAvailable(): boolean { return this.localAvailable; }
  get unsaved(): boolean { return this.dirty; }
  update(change: (project: Project) => Project): void { this.current = change(this.current); this.edited(); }
  rename(value: string): void { this.name = value; this.edited(); }
  private edited(): void {
    this.generation++; this.dirty = true; this.queueLocal();
    this.changed(this.remote ? "conflict" : this.localAvailable ? "pending" : "recovery-unavailable");
    if (!this.remote) this.schedule(500);
  }
  private queueLocal(): void {
    const recovery: Recovery = { key: this.recoveryKey, owner: this.owner, id: this.id, client: this.client, title: this.name,
      revision: this.stored.revision, generation: this.generation, document: this.current, updatedAt: Date.now() };
    this.local = this.local.then(() => persist(recovery)).catch(() => { this.localAvailable = false; this.changed("recovery-unavailable"); });
  }
  private schedule(delay: number): void { if (this.timer) clearTimeout(this.timer); if (!this.stopped) this.timer = setTimeout(() => void this.flush(), delay); }
  flush(): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    if (this.saving) return this.saving;
    if (this.stopped || !this.dirty || this.remote) return this.local;
    this.saving = this.send().finally(() => { this.saving = null; });
    return this.saving;
  }
  private async send(): Promise<void> {
    await this.local;
    while (this.dirty && !this.stopped && !this.remote) {
      const generation = this.generation, document = this.current, title = this.name;
      this.changed("saving");
      try {
        let saved: DraftRecord;
        try { saved = checkedDraft(await api(`/api/drafts/${this.id}`, "PUT", { revision: this.stored.revision, title, document })); }
        catch (error) {
          if (!(error instanceof ApiError) || error.status !== 409) throw error;
          const current = checkedDraft(object(error.payload)?.current);
          // A lost HTTP response may conceal a successful save. Acknowledge that exact content.
          if (current.title === title && same(current.document, document)) saved = current;
          else { this.remote = current; this.changed("conflict"); return; }
        }
        this.stored = saved; this.retry = 1000;
        if (this.generation === generation) {
          this.dirty = false; this.current = { ...this.current, updatedAt: saved.document.updatedAt };
          this.local = this.local.then(() => this.clearRecovery()).catch(() => { this.localAvailable = false; });
          await this.local;
          this.changed(this.localAvailable ? "saved" : "recovery-unavailable");
        } else this.queueLocal();
      } catch {
        this.changed(this.localAvailable ? "offline" : "recovery-unavailable");
        this.schedule(this.retry); this.retry = Math.min(30_000, this.retry * 2); return;
      }
    }
  }
  async keepAsCopy(suffix: string): Promise<void> {
    if (!this.remote || this.resolving) return;
    this.resolving = true;
    try {
      await this.local;
      const generation = this.generation;
      const saved = await createDraft(crypto.randomUUID(), (this.name + suffix).slice(0, 255), this.fingerprint, this.current);
      const previousKey = this.recoveryKey, recoveredFrom = this.recoveredFrom;
      releaseRecovery(this.owner, this.id);
      this.stored = saved; if (!this.stopped) claimRecovery(this.owner, this.id);
      this.name = this.generation === generation ? saved.title : (this.name + suffix).slice(0, 255);
      this.remote = null; this.dirty = this.generation !== generation;
      this.current = { ...this.current, updatedAt: saved.document.updatedAt };
      this.recoveryKey = `${this.owner}:${this.id}:${this.client}`; this.recoveredFrom = null;
      // Durably move later edits to their new identity before erasing the old recovery.
      this.queueLocal(); await this.local;
      if (this.localAvailable) {
        try {
          await forget(previousKey);
          if (recoveredFrom) await forgetUnchanged(recoveredFrom);
        } catch { this.localAvailable = false; }
      }
      if (!this.dirty) {
        const acknowledgedKey = this.recoveryKey;
        this.local = this.local.then(() => forget(acknowledgedKey)).catch(() => { this.localAvailable = false; });
        await this.local;
      }
      this.changed(this.dirty ? this.localAvailable ? "pending" : "recovery-unavailable" : this.localAvailable ? "saved" : "recovery-unavailable");
      if (this.dirty) this.schedule(0);
    } finally { this.resolving = false; }
  }
  async useServer(): Promise<void> {
    if (!this.remote || this.resolving) return;
    this.resolving = true;
    try {
      const generation = this.generation;
      await this.local;
      if (this.generation !== generation) return;
      await this.clearRecovery();
      if (this.generation !== generation) { this.queueLocal(); this.changed("conflict"); return; }
      this.stored = this.remote; this.current = this.remote.document; this.name = this.remote.title;
      this.remote = null; this.dirty = false; this.changed("saved");
    } finally { this.resolving = false; }
  }
  private async clearRecovery(): Promise<void> {
    await forget(this.recoveryKey);
    if (this.recoveredFrom) { await forgetUnchanged(this.recoveredFrom); this.recoveredFrom = null; }
  }
  stop(): void { releaseRecovery(this.owner, this.id); this.stopped = true; if (this.timer) clearTimeout(this.timer); window.removeEventListener("online", this.online); live.delete(this); }
}
